import React, { useEffect, useRef, useState } from "react";
import JsBarcode from "jsbarcode";
import { jsPDF } from "jspdf";
import { X, Printer, Download, RotateCw } from "lucide-react";

/**
 * LabelPrintModal — 58×40 mm price label for thermal printers.
 *
 * Layout (based on the customer-provided reference):
 *   ┌──────────────────────────────────┐
 *   │       Наименование               │  ← product.name (auto-shrink)
 *   │       ABC123                     │  ← our_sku (fallback: sku)
 *   ├──────────────────────────────────┤
 *   │      ████████ BARCODE ████████   │  ← Code-128 of our_sku/sku
 *   │      1 234567 890128             │  ← digits under the bars
 *   ├──────────────────────────────────┤
 *   │  500 ₸ (Скидка 20%)              │  ← old price (strikethrough) — variant with discount
 *   │           000 ₸                  │  ← final price (bold, large)
 *   └──────────────────────────────────┘
 *
 * Two variants selectable in the modal:
 *   • «С скидкой» (only shown when discount>0 or old_price>price)
 *   • «Без скидки» — only the final price is printed, no strikethrough line
 *
 * Print flow: builds a hidden iframe with N copies of the label, applies
 *   `@page { size: 58mm 40mm; margin: 0 }` and calls iframe.print().
 * Thermal printers (Xprinter/Zebra/GK420) honour the @page size — no drivers
 * needed, standard browser print dialog will use the physical label roll.
 */
export default function LabelPrintModal({ product, onClose }) {
  const barcodeRef = useRef(null);
  const priceValue = Number(product.price) || 0;
  const oldPriceValue = Number(product.old_price) || 0;
  const discountValue = Number(product.discount) || 0;
  const hasDiscount = oldPriceValue > priceValue || discountValue > 0;
  // Discount % — prefer explicit discount, otherwise compute from prices
  const computedDiscount = hasDiscount
    ? (discountValue || (oldPriceValue > 0 ? Math.round((1 - priceValue / oldPriceValue) * 100) : 0))
    : 0;

  const [variant, setVariant] = useState(hasDiscount ? "with-discount" : "no-discount");
  const [copies, setCopies] = useState(1);
  // Landscape 58×40 is the DEFAULT. Storage key is versioned (`_v2`) so any
  // legacy "portrait" value from the earlier rotated-CSS fix gets discarded
  // and everyone lands on the reliable landscape default until they toggle.
  const [orientation, setOrientation] = useState(() => {
    try { return localStorage.getItem("label_orientation_v2") || "landscape"; } catch { return "landscape"; }
  });
  useEffect(() => {
    try { localStorage.setItem("label_orientation_v2", orientation); } catch { /* noop */ }
  }, [orientation]);
  // Некоторые термопринтеры подают этикетку «боком»: драйвер печатает
  // содержимое повёрнутым на 90°. Этот тумблер докручивает картинку заранее —
  // выбор запоминается, чтобы каждый раз не настраивать.
  const [rotate90, setRotate90] = useState(() => {
    try { return localStorage.getItem("label_rotate90") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("label_rotate90", rotate90 ? "1" : "0"); } catch { /* noop */ }
  }, [rotate90]);
  const barcodeValue = product.our_sku || product.sku || "0000000000000";
  // Геометрия превью: внешняя рамка = физическая этикетка, внутренний слой =
  // содержимое (при включённом повороте оно развёрнуто — что видно, то и печатается).
  const PV = 4; // px на мм в превью
  const pageWmm = orientation === "portrait" ? 40 : 58;
  const pageHmm = orientation === "portrait" ? 58 : 40;
  const designWmm = rotate90 ? pageHmm : pageWmm;
  const designHmm = rotate90 ? pageWmm : pageHmm;


  // Render the barcode SVG in the preview
  useEffect(() => {
    if (!barcodeRef.current) return;
    try {
      JsBarcode(barcodeRef.current, barcodeValue, {
        format: "CODE128",
        displayValue: true,
        fontSize: 14,
        height: 40,
        margin: 0,
        background: "#ffffff",
        lineColor: "#000000",
      });
    } catch (e) {
      // Silent — the preview will just be empty; user sees the SKU text and can still print
    }
  }, [barcodeValue]);

  // Renders one full label at exact physical pixel dimensions (203 DPI, the
  // thermal-printer standard). Returns { canvas, widthMm, heightMm, pngData }.
  // Both handlePrint() and handleDownloadPDF() call this — same visual result
  // in either path.
  const renderLabelCanvas = (dpi = 600) => {
    // 600 dpi по умолчанию: картинка уходит в PDF с запасом по разрешению,
    // поэтому термопринтер не «пикселит» текст и штрихкод.
    const px = (mm) => Math.round((mm / 25.4) * dpi);
    // Физический размер этикетки (страницы) — по выбранной ориентации.
    const pageWmm = orientation === "portrait" ? 40 : 58;
    const pageHmm = orientation === "portrait" ? 58 : 40;
    // Если принтер подаёт этикетку боком, рисуем содержимое в повёрнутых
    // габаритах и в конце докручиваем картинку на 90°.
    const widthMm  = rotate90 ? pageHmm : pageWmm;
    const heightMm = rotate90 ? pageWmm : pageHmm;
    const isPortrait = heightMm > widthMm;
    const W = px(widthMm);
    const H = px(heightMm);

    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#000000";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";

    const padX = px(2);
    const padY = px(1.5);
    const centerX = W / 2;

    // Product name — auto-shrink so the FULL name always fits.
    // Budget: how tall the name block is allowed to be (mm → px). Everything
    // below (SKU, barcode, price) needs its own space, so keep this small.
    const nameBudgetPx = px(isPortrait ? 14 : 11);
    const maxNameWidth = W - 2 * padX;
    const nameFamily = `-apple-system, "Segoe UI", Roboto, Arial, sans-serif`;
    const nameLayout = fitNameLayout(ctx, product.name || "", {
      maxWidth: maxNameWidth,
      maxHeight: nameBudgetPx,
      initialFontPx: isPortrait ? px(3.4) : px(3.8),
      minFontPx: Math.max(8, Math.round(px(1.6))),
      weight: 600,
      family: nameFamily,
    });
    ctx.font = `600 ${nameLayout.fontPx}px ${nameFamily}`;
    let cursorY = padY;
    for (const l of nameLayout.lines) { ctx.fillText(l, centerX, cursorY); cursorY += nameLayout.lineH; }

    const skuFontPx = px(2.6);
    ctx.font = `${skuFontPx}px "Courier New", monospace`;
    ctx.fillText(barcodeValue, centerX, cursorY + px(0.4));
    cursorY += skuFontPx + px(1.2);

    const barcodeCanvas = document.createElement("canvas");
    try {
      JsBarcode(barcodeCanvas, barcodeValue, {
        format: "CODE128",
        displayValue: true,
        fontSize: Math.max(10, Math.round(px(2.6))),
        height: Math.max(30, Math.round(px(isPortrait ? 12 : 10))),
        margin: 0,
        background: "#ffffff",
        lineColor: "#000000",
      });
    } catch (_err) {
      barcodeCanvas.width = px(30); barcodeCanvas.height = px(10);
    }
    const priceHeightPx = px(isPortrait ? 8 : 7);
    const availableBarcodeH = H - cursorY - priceHeightPx - padY;
    const bcAspect = barcodeCanvas.width / barcodeCanvas.height;
    let bcH = Math.min(availableBarcodeH, px(isPortrait ? 18 : 14));
    let bcW = bcH * bcAspect;
    if (bcW > W - 2 * padX) { bcW = W - 2 * padX; bcH = bcW / bcAspect; }
    ctx.drawImage(barcodeCanvas, (W - bcW) / 2, cursorY, bcW, bcH);
    cursorY += bcH + px(1);

    const showDiscount = variant === "with-discount" && hasDiscount;
    const fmt = (n) => Math.round(n).toLocaleString("ru-RU") + " ₸";
    if (showDiscount) {
      const smallFontPx = px(2.4);
      ctx.font = `600 ${smallFontPx}px -apple-system, "Segoe UI", Arial, sans-serif`;
      const oldTxt = fmt(oldPriceValue);
      const discTxt = ` (Скидка ${computedDiscount}%)`;
      ctx.textAlign = "left";
      const oldW = ctx.measureText(oldTxt).width;
      ctx.font = `${smallFontPx}px -apple-system, "Segoe UI", Arial, sans-serif`;
      const discW = ctx.measureText(discTxt).width;
      const startX = centerX - (oldW + discW) / 2;
      ctx.font = `600 ${smallFontPx}px -apple-system, "Segoe UI", Arial, sans-serif`;
      ctx.fillText(oldTxt, startX, cursorY);
      ctx.fillRect(startX, cursorY + smallFontPx * 0.55, oldW, Math.max(1, Math.round(smallFontPx * 0.08)));
      ctx.font = `${smallFontPx}px -apple-system, "Segoe UI", Arial, sans-serif`;
      ctx.fillText(discTxt, startX + oldW, cursorY);
      ctx.textAlign = "center";
      cursorY += smallFontPx + px(0.5);
    }
    const finalFontPx = px(isPortrait ? 4 : 4.6);
    ctx.font = `800 ${finalFontPx}px -apple-system, "Segoe UI", Arial, sans-serif`;
    const finalY = H - padY - finalFontPx;
    ctx.fillText(fmt(priceValue), centerX, Math.max(cursorY, finalY));

    if (!rotate90) {
      return { canvas, widthMm, heightMm, pngData: canvas.toDataURL("image/png") };
    }
    // Докручиваем на 90°: страница остаётся физического размера этикетки,
    // а содержимое разворачивается — компенсация «боковой» подачи принтера.
    const out = document.createElement("canvas");
    out.width = px(pageWmm);
    out.height = px(pageHmm);
    const octx = out.getContext("2d");
    octx.fillStyle = "#ffffff";
    octx.fillRect(0, 0, out.width, out.height);
    octx.translate(out.width, 0);
    octx.rotate(Math.PI / 2);
    octx.drawImage(canvas, 0, 0);
    return { canvas: out, widthMm: pageWmm, heightMm: pageHmm,
              pngData: out.toDataURL("image/png") };
  };

  // BULLETPROOF: PDF at exact page size. Browsers cannot mangle a PDF page
  // size the way they mangle HTML @page. Users open the file in their printer
  // utility (BarTender / Xprinter / Zebra Setup / macOS Preview / Adobe) with
  // "Actual size" and it prints on ONE physical label. This is the way.
  const handleDownloadPDF = () => {
    const { widthMm, heightMm, pngData } = renderLabelCanvas(600);
    const copiesN = Math.max(1, Math.min(50, parseInt(copies) || 1));
    const pdf = new jsPDF({
      orientation: widthMm > heightMm ? "landscape" : "portrait",
      unit: "mm",
      format: [widthMm, heightMm],
      compress: true,
    });
    for (let i = 0; i < copiesN; i++) {
      if (i > 0) pdf.addPage([widthMm, heightMm], widthMm > heightMm ? "landscape" : "portrait");
      pdf.addImage(pngData, "PNG", 0, 0, widthMm, heightMm, undefined, "FAST");
    }
    const safeSku = String(barcodeValue).replace(/[^\w\-]/g, "_");
    pdf.save(`label_${safeSku}_${widthMm}x${heightMm}mm.pdf`);
  };

  // Печать ВЕКТОРОМ: текст остаётся текстом, штрихкод — SVG. Драйвер
  // растрирует их в родном разрешении печатающей головки, поэтому нет
  // «пиксельной» картинки, как при печати PNG.
  const buildLabelHtml = () => {
    const dpi = 203;
    const pxPerMm = dpi / 25.4;
    const toMm = (px) => +(px / pxPerMm).toFixed(3);
    const pageWmm = orientation === "portrait" ? 40 : 58;
    const pageHmm = orientation === "portrait" ? 58 : 40;
    const designWmm = rotate90 ? pageHmm : pageWmm;
    const designHmm = rotate90 ? pageWmm : pageHmm;
    const isPortrait = designHmm > designWmm;

    const family = `-apple-system, "Segoe UI", Roboto, Arial, sans-serif`;
    const measureCanvas = document.createElement("canvas");
    const mctx = measureCanvas.getContext("2d");
    const padXmm = 2;
    const nameLayout = fitNameLayout(mctx, product.name || "", {
      maxWidth: (designWmm - 2 * padXmm) * pxPerMm,
      maxHeight: (isPortrait ? 14 : 11) * pxPerMm,
      initialFontPx: (isPortrait ? 3.4 : 3.8) * pxPerMm,
      minFontPx: 1.6 * pxPerMm,
      weight: 600,
      family,
    });

    // Штрихкод как SVG — масштабируется без потери качества.
    let barcodeSvg = "";
    try {
      const holder = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      JsBarcode(holder, barcodeValue, {
        format: "CODE128", displayValue: true, fontSize: 16, height: 60,
        margin: 0, background: "#ffffff", lineColor: "#000000",
      });
      holder.removeAttribute("width");
      holder.removeAttribute("height");
      holder.setAttribute("preserveAspectRatio", "xMidYMid meet");
      barcodeSvg = new XMLSerializer().serializeToString(holder);
    } catch { barcodeSvg = ""; }

    const showDiscount = variant === "with-discount" && hasDiscount;
    const fmt = (n) => Math.round(n).toLocaleString("ru-RU") + " ₸";
    const nameHtml = nameLayout.lines.map(l => `<div>${escapeHtml(l)}</div>`).join("");

    return { pageWmm, pageHmm, designWmm, designHmm, body: `
      <div class="label">
        <div class="name">${nameHtml}</div>
        <div class="sku">${escapeHtml(barcodeValue)}</div>
        <div class="barcode">${barcodeSvg}</div>
        ${showDiscount ? `<div class="old"><s>${escapeHtml(fmt(oldPriceValue))}</s> (Скидка ${computedDiscount}%)</div>` : ""}
        <div class="price">${escapeHtml(fmt(priceValue))}</div>
      </div>`,
      css: `
      @page { size: ${pageWmm}mm ${pageHmm}mm; margin: 0; }
      html, body { margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .page { width: ${pageWmm}mm; height: ${pageHmm}mm; position: relative; overflow: hidden;
              page-break-after: always; break-after: page; }
      .page:last-child { page-break-after: auto; break-after: auto; }
      .label { position: absolute; top: 0; left: 0; box-sizing: border-box;
               width: ${designWmm}mm; height: ${designHmm}mm;
               padding: 1.5mm ${padXmm}mm 1.2mm; display: flex; flex-direction: column;
               align-items: center; text-align: center; font-family: ${family}; color: #000;
               ${rotate90 ? `transform-origin: 0 0; transform: translateX(${pageWmm}mm) rotate(90deg);` : ""} }
      .name { font-weight: 600; font-size: ${toMm(nameLayout.fontPx)}mm;
              line-height: ${toMm(nameLayout.lineH)}mm; width: 100%; }
      .sku { font-family: "Courier New", monospace; font-size: 2.6mm; margin-top: 0.6mm; letter-spacing: 0.02em; }
      .barcode { flex: 1; width: 100%; display: flex; align-items: center; justify-content: center;
                  min-height: 0; padding: 0.8mm 0; }
      .barcode svg { width: 100%; height: 100%; max-height: ${isPortrait ? 20 : 15}mm; }
      .old { font-size: 2.4mm; }
      .price { font-weight: 800; font-size: ${isPortrait ? 4.6 : 5}mm; letter-spacing: 0.01em; margin-top: 0.4mm; }
    ` };
  };

  const handlePrint = () => {
    const { body, css } = buildLabelHtml();
    const copiesN = Math.max(1, Math.min(50, parseInt(copies) || 1));
    const pages = Array.from({ length: copiesN },
      () => `<div class="page">${body}</div>`).join("");

    const doc = `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <title>Print — ${escapeHtml(barcodeValue)}</title>
  <style>${css}</style>
</head>
<body>${pages}</body>
</html>`;

    // Insert an iframe, wait for load, then print. Remove after print dialog closes.
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);
    iframe.contentDocument.open();
    iframe.contentDocument.write(doc);
    iframe.contentDocument.close();
    // Give the browser a moment to layout the SVG barcode before printing
    setTimeout(() => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (e) { /* noop */ }
      setTimeout(() => { try { document.body.removeChild(iframe); } catch { /* noop */ } }, 1500);
    }, 300);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 250,
                  display: "flex", alignItems: "flex-start", justifyContent: "center",
                  padding: "20px", overflowY: "auto" }} onClick={onClose}>
      <div data-testid="label-print-modal" onClick={(e) => e.stopPropagation()}
           style={{ background: "white", borderRadius: "12px", width: "100%", maxWidth: "520px",
                    padding: "22px", marginTop: "20px", color: "#0A0A0A" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
          <div>
            <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: "22px", fontWeight: 400, margin: 0 }}>Печать этикетки</h3>
            <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
              Формат <b>{orientation === "portrait" ? "40 × 58 мм (книжная)" : "58 × 40 мм (альбомная)"}</b> · для термопринтеров
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#888" }}><X size={20} /></button>
        </div>

        {/* Orientation toggle — must match the physical label roll in the printer */}
        <div style={{ marginBottom: "14px" }}>
          <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#6b7280", marginBottom: "6px", letterSpacing: "0.06em", textTransform: "uppercase" }}>Ориентация этикетки</div>
          <div style={{ display: "flex", gap: "8px" }}>
            <VariantBtn active={orientation === "portrait"}
              onClick={() => setOrientation("portrait")}
              label="Книжная 40 × 58" testid="label-orient-portrait" />
            <VariantBtn active={orientation === "landscape"}
              onClick={() => setOrientation("landscape")}
              label="Альбомная 58 × 40" testid="label-orient-landscape" />
          </div>
          <div style={{ fontFamily: "Manrope", fontSize: "10.5px", color: "#8a8a89", marginTop: "6px", lineHeight: 1.4 }}>
            Выберите ориентацию, соответствующую рулону этикеток в вашем принтере.
          </div>
          <button data-testid="label-rotate-toggle" onClick={() => setRotate90(v => !v)}
            style={{ marginTop: "10px", width: "100%",
                      background: rotate90 ? "#0A0A0A" : "white",
                      color: rotate90 ? "#D4B878" : "#0A0A0A",
                      border: `1px solid ${rotate90 ? "#C5A059" : "#d1d5db"}`,
                      borderRadius: "6px", padding: "10px 12px", cursor: "pointer",
                      fontFamily: "Manrope", fontSize: "12.5px", fontWeight: 700,
                      display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
            <RotateCw size={14} />
            {rotate90 ? "Поворот на 90° включён" : "Печатает повёрнуто? Повернуть на 90°"}
          </button>
          <div style={{ fontFamily: "Manrope", fontSize: "10.5px", color: "#8a8a89", marginTop: "6px", lineHeight: 1.4 }}>
            {rotate90
              ? "Содержимое уходит на принтер повёрнутым — так печатают принтеры с боковой подачей этикетки. Настройка запомнена."
              : "Если наклейка выходит боком, включите этот тумблер — печать выровняется. Настройка запомнится."}
          </div>
        </div>

        {/* Critical browser-print settings tip — Chrome/Edge silently fall back
            to A4 unless the user explicitly picks the physical label size in
            the OS print dialog. Without this the label spreads across 2-3
            physical labels. Show a clear step-by-step. */}
        <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: "6px",
                      padding: "10px 12px", marginBottom: "14px",
                      fontFamily: "Manrope", fontSize: "11.5px", color: "#78350F", lineHeight: 1.5 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>⚠️ В окне печати браузера обязательно:</div>
          <div>1. Принтер — <b>ваш термопринтер</b> (не «Save as PDF»)</div>
          <div>2. Бумага / Paper size — <b>Custom {orientation === "portrait" ? "40 × 58" : "58 × 40"} мм</b></div>
          <div>3. Поля / Margins — <b>Нет / None</b>. Масштаб — <b>100%</b> (не «По размеру страницы»)</div>
          <div>4. Снимите галочку «Верхние и нижние колонтитулы» (Headers/Footers)</div>
        </div>

        {/* Variant toggle */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
          <VariantBtn active={variant === "with-discount"} disabled={!hasDiscount}
            onClick={() => setVariant("with-discount")} label="Со скидкой" testid="label-variant-discount" />
          <VariantBtn active={variant === "no-discount"}
            onClick={() => setVariant("no-discount")} label="Без скидки" testid="label-variant-plain" />
        </div>
        {!hasDiscount && (
          <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#8a8a89", marginTop: "-8px", marginBottom: "12px", lineHeight: 1.4 }}>
            У товара не задана старая цена или скидка — доступен только вариант «без скидки».
          </div>
        )}

        {/* Live preview — 1:1 scale at ~2.5px per mm for on-screen legibility.
            Dimensions swap with orientation so the manager sees exactly what
            will print on the physical label. */}
        <div style={{ background: "#f5f5f4", padding: "16px", borderRadius: "8px", display: "flex", justifyContent: "center", marginBottom: "16px" }}>
          <div data-testid="label-preview"
               style={{ width: `${pageWmm * PV}px`, height: `${pageHmm * PV}px`,
                        background: "white", boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                        borderRadius: "2px", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: 0, left: 0,
                        width: `${designWmm * PV}px`, height: `${designHmm * PV}px`,
                        boxSizing: "border-box", padding: "8px 8px 6px",
                        display: "flex", flexDirection: "column",
                        transformOrigin: "0 0",
                        transform: rotate90 ? `translateX(${pageWmm * PV}px) rotate(90deg)` : "none" }}>
            <PreviewName
              text={product.name || ""}
              maxWidth={designWmm * PV - 16}
              maxHeight={designHmm > designWmm ? 56 : 44}
              initialFontPx={13}
            />
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "10px", marginTop: "2px", fontFamily: "monospace", letterSpacing: "0.03em" }}>
                {barcodeValue}
              </div>
            </div>
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "4px 0" }}>
              <svg ref={barcodeRef} style={{ maxWidth: "100%", maxHeight: "48px" }} />
            </div>
            <div style={{ textAlign: "center", marginTop: "2px" }}>
              {variant === "with-discount" && hasDiscount ? (
                <>
                  <div style={{ fontSize: "10px" }}>
                    <span style={{ textDecoration: "line-through", fontWeight: 600 }}>{Math.round(oldPriceValue).toLocaleString("ru-RU")} ₸</span>
                    <span> (Скидка {computedDiscount}%)</span>
                  </div>
                  <div style={{ fontSize: "17px", fontWeight: 800, letterSpacing: "0.02em" }}>{Math.round(priceValue).toLocaleString("ru-RU")} ₸</div>
                </>
              ) : (
                <div style={{ fontSize: "18px", fontWeight: 800, letterSpacing: "0.02em", paddingTop: "6px" }}>{Math.round(priceValue).toLocaleString("ru-RU")} ₸</div>
              )}
            </div>
          </div>
          </div>
        </div>

        {/* Copies */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px" }}>
          <label style={{ fontFamily: "Manrope", fontSize: "12px", color: "#374151" }}>Копий:</label>
          <input type="number" min="1" max="50" data-testid="label-copies-input"
                 value={copies} onChange={(e) => setCopies(e.target.value)}
                 style={{ width: "80px", padding: "6px 8px", border: "1px solid #e5e7eb", borderRadius: "4px",
                          fontFamily: "Manrope", fontSize: "13px" }} />
          <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#8a8a89" }}>от 1 до 50</div>
        </div>

        <div style={{ display: "flex", gap: "10px" }}>
          <button onClick={onClose} style={{ background: "#f9fafb", border: "1px solid #e5e7eb",
                    borderRadius: "6px", padding: "12px 14px", cursor: "pointer", fontFamily: "Manrope", fontSize: "13px" }}>
            Отмена
          </button>
          <button data-testid="label-print-btn" onClick={handlePrint}
            title="Печать на термопринтер через диалог браузера"
            style={{ flex: 3, background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A",
                    border: "none", borderRadius: "6px", padding: "12px", cursor: "pointer",
                    fontFamily: "Manrope", fontWeight: 700, fontSize: "13px",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
            <Printer size={15} /> Печать
          </button>
          <button data-testid="label-download-pdf-btn" onClick={handleDownloadPDF}
            title="Скачать PDF — например, для печати из утилиты принтера"
            style={{ flex: 1, background: "white", color: "#0A0A0A",
                    border: "1px solid #d1d5db", borderRadius: "6px", padding: "12px", cursor: "pointer",
                    fontFamily: "Manrope", fontWeight: 600, fontSize: "12px",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
            <Download size={13} /> PDF
          </button>
        </div>

        <div style={{ fontFamily: "Manrope", fontSize: "10.5px", color: "#6b7280", marginTop: "10px", textAlign: "center", lineHeight: 1.4 }}>
          💡 <b>Печать</b> — обычная печать на термопринтер. <b>PDF</b> — если удобнее печатать из утилиты принтера (Actual size / 100%).
        </div>
      </div>
    </div>
  );
}

/**
 * Preview-only mirror of the print `fitNameLayout` algorithm. Uses an offscreen
 * <canvas> to measure text so the on-screen preview shrinks the font in the
 * exact same way as the printed label — no ellipsis, always the full name.
 */
function PreviewName({ text, maxWidth, maxHeight, initialFontPx }) {
  const family = `-apple-system, "Segoe UI", Roboto, Arial, sans-serif`;
  const canvas = React.useMemo(
    () => (typeof document !== "undefined" ? document.createElement("canvas") : null),
    []
  );
  const layout = React.useMemo(() => {
    if (!canvas) return { lines: [text], fontPx: initialFontPx, lineH: Math.round(initialFontPx * 1.08) };
    const ctx = canvas.getContext("2d");
    return fitNameLayout(ctx, text, {
      maxWidth, maxHeight,
      initialFontPx, minFontPx: 7,
      weight: 600, family,
    });
  }, [canvas, text, maxWidth, maxHeight, initialFontPx, family]);
  return (
    <div style={{
      textAlign: "center",
      fontSize: `${layout.fontPx}px`,
      lineHeight: `${layout.lineH}px`,
      fontWeight: 600,
      fontFamily: family,
      minHeight: `${maxHeight}px`,
    }}>
      {layout.lines.map((l, i) => <div key={i}>{l}</div>)}
    </div>
  );
}

function VariantBtn({ active, disabled, onClick, label, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled}
      style={{ flex: 1, background: active ? "linear-gradient(135deg,#C5A059,#D4B878)" : "white",
               color: active ? "#0A0A0A" : (disabled ? "#c7c7c6" : "#374151"),
               border: `1px solid ${active ? "#C5A059" : "#e5e7eb"}`,
               borderRadius: "6px", padding: "10px 12px",
               cursor: disabled ? "not-allowed" : "pointer",
               fontFamily: "Manrope", fontSize: "12px", fontWeight: active ? 700 : 500,
               opacity: disabled ? 0.55 : 1 }}>
      {label}
    </button>
  );
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Word-wrap for canvas — greedy fill up to maxWidth per line.
// Returns EVERY word (no truncation). If a single token is wider than
// `maxWidth`, break it character-wise so we never overflow the label edge.
function wrapAllLines(ctx, text, maxWidth) {
  const tokens = String(text || "").split(/\s+/).filter(Boolean);
  const out = [];
  let cur = "";
  const flush = () => { if (cur) { out.push(cur); cur = ""; } };
  const pushWord = (word) => {
    if (ctx.measureText(word).width <= maxWidth) {
      const candidate = cur ? `${cur} ${word}` : word;
      if (ctx.measureText(candidate).width <= maxWidth) { cur = candidate; }
      else { flush(); cur = word; }
      return;
    }
    // Word longer than the whole label — chop into characters.
    flush();
    let chunk = "";
    for (const ch of word) {
      const c2 = chunk + ch;
      if (ctx.measureText(c2).width <= maxWidth) { chunk = c2; }
      else { out.push(chunk); chunk = ch; }
    }
    if (chunk) cur = chunk;
  };
  for (const w of tokens) pushWord(w);
  flush();
  return out;
}

/**
 * Iteratively shrink the product-name font until the WHOLE name fits within
 * `maxWidth` × `maxHeight`. No ellipsis — every character stays visible.
 *
 * Returns { lines, fontPx, lineH } — caller must set ctx.font itself when it
 * needs to draw (fitNameLayout mutates ctx.font during measurement).
 */
function fitNameLayout(ctx, text, opts) {
  const {
    maxWidth, maxHeight,
    initialFontPx, minFontPx = 8,
    weight = 600, family = "sans-serif",
    lineHeightRatio = 1.08,
  } = opts;
  let fontPx = Math.max(minFontPx, Math.round(initialFontPx));
  let lines = [];
  let lineH = Math.round(fontPx * lineHeightRatio);
  while (fontPx >= minFontPx) {
    ctx.font = `${weight} ${fontPx}px ${family}`;
    lines = wrapAllLines(ctx, text, maxWidth);
    lineH = Math.round(fontPx * lineHeightRatio);
    if (lines.length * lineH <= maxHeight) return { lines, fontPx, lineH };
    fontPx -= 1;
  }
  // Even at min size the name overflows — return the best-effort layout.
  ctx.font = `${weight} ${minFontPx}px ${family}`;
  lines = wrapAllLines(ctx, text, maxWidth);
  lineH = Math.round(minFontPx * lineHeightRatio);
  return { lines, fontPx: minFontPx, lineH };
}
